import {
  Box,
  Button,
  Flex,
  IconButton,
  Image,
  Switch,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';
import {
  CircleCheck,
  DateFilter,
  durationData,
  InfoIcon,
  MetricTooltip,
  PageHeader,
  PeriodType,
  RefreshIcon,
  removeEmptyQueryParams,
  useDashboardStore,
  useMetricsListData,
  useQueryState,
  useTeamComparisonStore,
} from '@devd-client/devd/components';
import TeamCompHeatMap from '../components/TeamCompHeatMap';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { defaultMetrics } from '../helpers/team-details.utils';
import { useFetchMetricList } from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import ColorScheme from '../assets/colorScale.jpg';
import { useTeamDetailsStore } from '../store/teamDetail';

export const TeamComparison = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [appState, dispatch] = useAuth();
  const { selected, setSelected } = useDashboardStore();
  const { triggerApiCall } = useTeamDetailsStore();
  const { data: metricsList, isLoading } = useFetchMetricList();

  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [periodOptions, setPeriodOptions] =
    useState<PeriodType[]>(durationData);
  const { isChecked, toggleSwitch, setSelectedTeams, setSelectedMetrics } =
    useTeamComparisonStore();

  const processedMetrics = useMemo(() => {
    if (!metricsList?.dto) return [];

    return metricsList.dto.flatMap((metric: MetricTooltip) => {
      if (metric.isPerMemberMetric) {
        const memberMetric = {
          ...metric,
          id: `${metric.metric}_PER_MEMBER`,
          title: `${metric.title}/Member`,
          info: `${metric.info} (Avg per member)`,
        };
        return [
          {
            ...metric,
            id: metric.metric === 'CUSTOM' ? metric.title : metric.metric,
          },
          memberMetric,
        ];
      }
      return [
        {
          ...metric,
          id: metric.metric === 'CUSTOM' ? metric.title : metric.metric,
        },
      ];
    });
  }, [metricsList?.dto]);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }
  }, [selected]);

  useEffect(() => {
    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  const handleUndo = () => {
    const allTeam = { value: 'all', label: 'All Teams' };
    setSelectedMetrics(
      processedMetrics?.filter((el: MetricTooltip) =>
        defaultMetrics.includes(el.name)
      )
    );
    setSelectedTeams([allTeam]);

    if (isChecked === false) {
      toggleSwitch();
    }
  };

  return (
    <Flex w="full" direction="column" gap={4}>
      <Flex direction="column">
        <PageHeader
          headingText="Team Comparison"
          tooltip=" Analyze and benchmark performance metrics across multiple development teams side by side. Select specific teams and metrics that matter most to your organization like PR merge rates, issue resolution, cycle times to identify strengths and opportunities. Color-coded percentile indicators help you quickly spot high and low performers relative to organizational averages. Use this data to share best practices between teams, and make informed decisions about resource allocation and process improvements."
          // component={
          //   <Flex gap={2}>
          //     <IconButtonContainer withGradient>
          //       <AiStarIcon />
          //     </IconButtonContainer>
          //     <IconButtonContainer>
          //       <DownloadIcon />
          //     </IconButtonContainer>
          //   </Flex>
          // }
        />
        <Box>
          {!pathname.includes('compare-teams') && (
            <Button variant="link" onClick={() => navigate(-1)}>
              Back
            </Button>
          )}
        </Box>
      </Flex>

      <Flex mt={4} align="flex-end" justify={'space-between'} w="full">
        <Flex gap={2} height="32px" align="center">
          <DateFilter
            getUser={getUser}
            appState={appState}
            dispatch={dispatch}
            AuthActionTypes={AuthActionTypes}
          />

          <Tooltip hasArrow label="Set to Default">
            <IconButton
              bg="transparent"
              border="1px solid #E0E0E0"
              borderRadius="4px"
              aria-label="circle"
              onClick={handleUndo}
              height="32px"
              icon={<CircleCheck />}
            />
          </Tooltip>
          <Tooltip hasArrow label="Refresh">
            <IconButton
              bg="transparent"
              border="1px solid #E0E0E0"
              borderRadius="4px"
              aria-label="circle"
              onClick={triggerApiCall}
              height="32px"
              icon={<RefreshIcon />}
            />
          </Tooltip>
        </Flex>

        <Flex height="60px" width="280px">
          <Image src={ColorScheme} alt="color scheme" />
        </Flex>

        <Flex align="center">
          <Text
            fontFamily={'heading'}
            fontSize="sm"
            fontWeight={'medium'}
            color="text.secondary2"
            mr={1}
          >
            Compare to Organisation
          </Text>
          <Box mr={2}>
            <Tooltip
              label="This allows you to either compare your selected Teams with each other or Compare Teams with organisation averages"
              hasArrow
              placement="bottom-end"
            >
              <Box>
                <InfoIcon />
              </Box>
            </Tooltip>
          </Box>
          <Box>
            <Switch isChecked={isChecked} onChange={toggleSwitch} />
          </Box>
        </Flex>
      </Flex>

      <Box>
        <TeamCompHeatMap
          isChecked={isChecked}
          metricsList={processedMetrics}
          isLoading={isLoading}
        />
      </Box>
    </Flex>
  );
};

export default TeamComparison;
